<template>
  <div class="wizard-step-content step3-finish">
    <!-- Summary Section -->
    <div class="step3-summary" v-if="!duplicationStarted || duplicationStatus !== ''">
      <h3 class="step3-title">{{ stepTitle }}</h3>

      <!-- Project Options Card -->
      <div class="summary-card" v-if="false">
        <div class="summary-card__header">
          <i class="fas fa-cog"></i> Project Options
        </div>
        <div class="summary-card__body">
          <div class="summary-options-grid">
            <div
              v-for="opt in displayOptions"
              :key="opt.key"
              class="summary-option"
              :class="{ 'summary-option--enabled': opt.enabled, 'summary-option--disabled': !opt.enabled }"
            >
              <i :class="opt.enabled ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-muted'"></i>
              <span>{{ opt.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Selected Deliverables Card -->
      <div class="summary-card">
        <!-- <div class="summary-card__header">
          <i class="fas fa-list-ul"></i> Selected Deliverables
          <span class="summary-card__count">{{ selectedDeliverables.length }}</span>
        </div> -->
        <div class="summary-card__body">
          <div class="summary-item summary-item--highlight summary-title">
            <strong>{{ wizard.projectOptions.title }}</strong>
            
            <!-- Action Button -->
            <div class="step3-action" v-if="!['completed', 'failed'].includes(duplicationStatus)">
              <button
                class="btn btn-primary btn-duplicate"
                :disabled="duplicationStarted"
                @click="startDuplication"
              >
                <!-- <i v-if="!duplicationStarted" class="fas fa-copy mr-2"></i> -->
                <img v-if="!duplicationStarted" src="@/csmworkflow/img/duplication.svg" />
                <b-spinner v-if="duplicationStarted && duplicationStatus !== 'failed'" small class="mr-2"></b-spinner>
                {{ duplicationStarted ? 'Duplicating...' : 'Start Duplication' }}
              </button>
            </div>
          </div>

          <div v-if="!duplicationStarted">
            <div class="summary-item summary-item--highlight-sm">
              <strong>Selected Deliverables</strong>
            </div>

            <div v-if="selectedDeliverables.length === 0" class="text-muted p-2">
              No deliverables selected.
            </div>
            <div
              v-for="deliv in selectedDeliverables"
              :key="deliv.id"
              class="summary-deliverable"
            >
              <span class="summary-deliverable__title">
                <!-- <i class="fas fa-file-alt"></i> -->
                {{ deliv.title }}
              </span>
              
              <div class="summary-deliverable-right">
                  <span v-if="!isOSMP(deliv)" class="summary-deliverable__type">Project Deliverable</span>
              <!-- <span class="summary-deliverable__type">{{ deliv.template_title }}</span> -->
              <span
                v-if="isOSMP(deliv)"
                class="summary-deliverable__posts"
              >
                <!-- <i class="fa fa-share-alt"></i> -->
                {{ getSelectedPostCount(deliv) }} / {{ getTotalPostCount(deliv) }} {{ getPostCountLabel(deliv) }}
              </span>
              </div>
            </div>
          </div>

          <!-- Totals -->
          <div class="step3-totals" v-if="false">
            <span>{{ selectedDeliverables.length }} deliverable{{ selectedDeliverables.length !== 1 ? 's' : '' }}</span>
            <span v-if="totalSelectedPosts > 0" class="ml-3">
              &middot; {{ totalSelectedPosts }} social media post{{ totalSelectedPosts !== 1 ? 's' : '' }}
            </span>
          </div>

          <!-- Progress Section -->
          <div class="step3-progress" v-if="duplicationStarted">
            <!-- Queue waiting -->
            <div v-if="queueMessage && duplicationStatus === 'pending'" class="step3-queue-msg text-center">
              <p class="mt-3">{{ queueMessage }}</p>
              <p class="text-muted"><small>Another project is being duplicated. Your request will begin shortly.</small></p>
            </div>

            <!-- Progress bar -->
            <div v-if="duplicationStatus === 'processing' || duplicationStatus === 'pending'" class="step3-progress-bar">
              <b-progress :value="progressPercent" class="mb-2" height="20px" :variant="'success'"></b-progress>
              <div class="step3-progress-info">
                <span v-if="progressCount" class="step3-progress-count">{{ progressCount }}</span>
                <span class="step3-progress-message">{{ progressMessage }}</span>
                <span v-if="progressLabel" class="step3-progress-label">{{ progressLabel }}</span>
              </div>
            </div>

            <!-- Completed -->
            <div v-if="duplicationStatus === 'completed'" class="step3-complete text-center">
              <i class="fas fa-check-circle text-success" style="font-size: 3rem;"></i>
              <p class="mt-3 step3-complete-message">{{ progressMessage || 'Project duplicated successfully!' }}</p>
              <button
                v-if="newProjectNumber"
                class="btn btn-duplicate btn-primary mt-2"
                @click="goToNewProject"
              >
                <!-- <i class="fas fa-external-link-alt mr-2"></i> -->
                 View New Project
              </button>
            </div>

            <!-- Failed -->
            <div v-if="duplicationStatus === 'failed'" class="step3-failed text-center">
              <i class="fas fa-times-circle text-danger" style="font-size: 3rem;"></i>
              <p class="mt-3" style="font-weight: bold;">A system error occurred. Duplication failed.</p>
              <p class="text-muted">An error notification has been sent to Pattern Interactive.</p>
              <button
                v-if="sourceProjectNumber"
                class="btn btn-cancel mt-2"
                @click="goToSourceProject"
              >
                <!-- <i class="fas fa-arrow-left mr-2"></i>  -->
                Back to Project
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";

export default {
  name: "DuplicateStep3Finish",
  data() {
    return {
      duplicationStatus: "",
      progressPercent: 0,
      progressMessage: "",
      progressDetails: null,
      queueMessage: "",
      newProjectNumber: null,
      jobKey: null,
    };
  },
  computed: {
    ...mapState({
      wizard: (state) => state.duplicationWizard,
    }),
    ...mapGetters(["duplicationWizard"]),
    duplicationStarted() {
      return this.wizard.duplicationStarted;
    },
    selectedDeliverables() {
      const ids = this.wizard.selectedDeliverableIds;
      return this.wizard.allDeliverables.filter((d) => ids.includes(d.id));
    },
    totalSelectedPosts() {
      return this.selectedDeliverables.reduce((total, deliverable) => {
        if (!this.isOSMP(deliverable)) return total;
        return total + this.getSelectedPostCount(deliverable);
      }, 0);
    },
    displayOptions() {
      const opts = this.wizard.projectOptions;
      return [
        { key: "department", label: "Department", enabled: opts.department },
        { key: "deadline", label: "Deadline", enabled: opts.deadline },
        { key: "projectDetail", label: "Project Detail", enabled: opts.projectDetail },
        { key: "resources", label: "Resources", enabled: opts.resources },
        { key: "engagementManager", label: "Engagement Manager", enabled: opts.engagementManager },
        { key: "projectLead", label: "Project Lead", enabled: opts.projectLead },
        { key: "status", label: "Status", enabled: opts.status },
        { key: "audience", label: "Audience", enabled: opts.audience },
        { key: "budget", label: "Budget", enabled: opts.budget },
        { key: "requisitionNumber", label: "Requisition Number", enabled: opts.requisitionNumber },
        { key: "taskUsers", label: "Copy Task Users", enabled: opts.taskUsers },
        { key: "taskNotes", label: "Copy Task Notes", enabled: opts.taskNotes },
        { key: "taskDueDates", label: "Task Due Dates", enabled: opts.taskDueDates },
        { key: "deliverableDueDates", label: "Deliverable Due Dates", enabled: opts.deliverableDueDates },
      ];
    },
    progressCount() {
      if (!this.progressDetails || !this.progressDetails.total) return null;
      const completed = this.progressDetails.completed ?? 0;
      const total = this.progressDetails.total ?? 0;
      return `${completed}/${total}`;
    },
    progressLabel() {
      if (!this.progressDetails || !this.progressDetails.label) return null;
      return this.progressDetails.label;
    },
    sourceProjectNumber() {
      return this.wizard.sourceProject && this.wizard.sourceProject.project_number
        ? this.wizard.sourceProject.project_number
        : null;
    },
    stepTitle() {
      if (this.duplicationStatus === "completed") {
        return "Finished";
      }
      if (this.duplicationStarted && this.duplicationStatus !== "failed") {
        return "Duplicating...";
      }
      return "Review & Duplicate";
    },
  },
  watch: {
    duplicationStatus(status) {
      this.$emit("status-change", status);
    },
  },
  methods: {
    isOSMP(deliverable) {
      return deliverable.template_slug === "organic-social-media-plan";
    },
    isUnlinkedSeriesChild(post) {
      return !!(post && post.social_series_id && !post.is_master && post.manually_updated_at);
    },
    getSeriesMasterIds(deliverable) {
      if (!deliverable || !Array.isArray(deliverable.posts)) return new Set();
      return new Set(deliverable.posts.filter((post) => post.social_series_id && post.is_master).map((post) => post.id));
    },
    getTotalPostCount(deliverable) {
      if (!deliverable) return 0;
      if (!Array.isArray(deliverable.posts) || !deliverable.posts.length) {
        return deliverable.posts_count || 0;
      }
      return deliverable.posts.filter((post) => !post.social_series_id || !post.is_master).length;
    },
    getSelectedPostCount(deliverable) {
      if (!deliverable) return 0;
      const selectedIds = this.wizard.selectedSocialMediaPostIds[deliverable.id];
      if ((!Array.isArray(deliverable.posts) || !deliverable.posts.length) && !selectedIds) {
        return this.wizard.selectedDeliverableIds.includes(deliverable.id) ? (deliverable.posts_count || 0) : 0;
      }
      const posts = this.buildSelectedSocialMediaPostDuplicateIds()[deliverable.id] || [];
      const masterIds = this.getSeriesMasterIds(deliverable);
      return posts.filter((postId) => !masterIds.has(postId)).length;
    },
    getPostCountLabel(deliverable) {
      return this.getTotalPostCount(deliverable) === 1 ? "Post" : "Posts";
    },
    getPostCountOverride(counts, postId, fallback) {
      if (Object.prototype.hasOwnProperty.call(counts, postId)) {
        let count = parseInt(counts[postId], 10);
        return isNaN(count) || count < 0 ? 0 : count;
      }
      return fallback;
    },
    buildSelectedSocialMediaPostDuplicateIds() {
      const result = {};

      this.selectedDeliverables.forEach((deliverable) => {
        if (!this.isOSMP(deliverable) || !Array.isArray(deliverable.posts)) return;

        const parentId = deliverable.id;
        const selectedIds = this.wizard.selectedSocialMediaPostIds[parentId] || [];
        const selectedCounts = (this.wizard.selectedSocialMediaPostCounts && this.wizard.selectedSocialMediaPostCounts[parentId]) || {};
        const seriesMap = {};
        const soloPosts = [];

        deliverable.posts.forEach((post) => {
          const seriesId = post.social_series_id;
          if (!seriesId || this.isUnlinkedSeriesChild(post)) {
            soloPosts.push(post);
            return;
          }
          if (!seriesMap[seriesId]) {
            seriesMap[seriesId] = { master: null, children: [] };
          }
          if (post.is_master) {
            seriesMap[seriesId].master = post;
          } else {
            seriesMap[seriesId].children.push(post);
          }
        });

        const sequence = [];

        soloPosts.forEach((post) => {
          if (!selectedIds.includes(post.id)) return;
          const count = this.getPostCountOverride(selectedCounts, post.id, 1);
          for (let i = 0; i < count; i++) {
            sequence.push(post.id);
          }
        });

        Object.values(seriesMap).forEach((group) => {
          group.children = group.children.slice().sort((a, b) => {
            const aDate = a.publish_date ? new Date(a.publish_date).getTime() : 0;
            const bDate = b.publish_date ? new Date(b.publish_date).getTime() : 0;
            return aDate - bDate;
          });

          if (!group.master) {
            group.children.forEach((post) => {
              if (selectedIds.includes(post.id)) {
                sequence.push(post.id);
              }
            });
            return;
          }

          if (Object.prototype.hasOwnProperty.call(selectedCounts, group.master.id)) {
            let count = parseInt(selectedCounts[group.master.id], 10);
            if (isNaN(count) || count < 0) count = 0;
            if (count > 0) {
              sequence.push(group.master.id);
              for (let i = 0; i < count && group.children.length > 0; i++) {
                sequence.push(group.children[i % group.children.length].id);
              }
            }
            return;
          }

          if (selectedIds.includes(group.master.id)) {
            sequence.push(group.master.id);
          }
          group.children.forEach((post) => {
            if (selectedIds.includes(post.id)) {
              sequence.push(post.id);
            }
          });
        });

        if (sequence.length > 0) {
          result[parentId] = sequence;
        }
      });

      return result;
    },
    async startDuplication() {
      this.$store.commit("SET_DUPLICATION_STARTED", true);
      this.duplicationStatus = "pending";
      this.progressPercent = 0;
      this.progressMessage = "Starting duplication...";
      this.progressDetails = null;

      // Listen for progress events
      this.$root.$on("duplicationJobProgress", this.handleJobProgress);
      this.$root.$on("duplicationJobInQueue", this.handleJobInQueue);

      // Add beforeunload warning
      window.addEventListener("beforeunload", this.beforeUnloadHandler);

      try {
        const sp = this.wizard.sourceProject || {};

        // Build projectOverrides with user edits from Step 1 so the duplicate
        // reflects the edited values (sourceProject was deep-cloned in store;
        // child components mutated this clone only, not the original project).
        const serializeCustomField = (field) => field ? {
          pfv_id: field.pfv_id || field.id || null,
          field_id: field.field_id,
          parent_id: field.parent_id || null,
          field_key: field.field_key || null,
          value:
            field.value !== undefined && field.value !== null && typeof field.value === "object"
              ? JSON.stringify(field.value)
              : field.value,
        } : null;
        const customFields = Array.isArray(sp.customFields) ? sp.customFields : [];
        const resourceFieldIds = [20, 21, 22, 37];
        const overrideCustomFields = customFields
          .filter(field => field && typeof field.field_id !== "undefined" && !resourceFieldIds.includes(Number(field.field_id)))
          .map(serializeCustomField);

        // Keep each resource's child fields grouped with its URL parent.
        const resourceOverrides = customFields
          .filter(field => field && Number(field.field_id) === 20)
          .map((linkUrl) => {
            const parentId = linkUrl.pfv_id || linkUrl.id;
            const childField = fieldId => customFields.find(field =>
              field && Number(field.field_id) === fieldId && String(field.parent_id) === String(parentId)
            );

            return {
              linkUrl: serializeCustomField(linkUrl),
              linkLabel: serializeCustomField(childField(21)),
              linkNotes: serializeCustomField(childField(22)),
              linkType: serializeCustomField(childField(37)),
            };
          });

        const extractId = (v) => {
          if (v === null || typeof v === "undefined") return null;
          if (typeof v === "object") return v.id || null;
          return v;
        };

        const projectOverrides = {
          title: this.wizard.projectOptions.title,
          engagement_manager: extractId(sp.engagement_manager),
          project_lead: extractId(sp.project_lead),
          project_leaders: Array.isArray(sp.project_leaders)
            ? sp.project_leaders.map(l => ({ id: extractId(l) })).filter(l => l.id)
            : null,
          due_date: sp.due_date || null,
          start_date: sp.start_date || null,
          departments: Array.isArray(sp.departments)
            ? sp.departments.map(d => extractId(d)).filter(id => id)
            : null,
          customFields: overrideCustomFields,
          resources: resourceOverrides,
        };

        const payload = {
          projectId: this.wizard.sourceProject.id,
          title: this.wizard.projectOptions.title,
          ...this.wizard.projectOptions,
          deliverables: this.wizard.selectedDeliverableIds.length > 0,
          taskDueDates: false,
          socialMediaPosts: true,
          selectedDeliverableIds: this.wizard.selectedDeliverableIds,
          selectedSocialMediaPostIds: this.wizard.selectedSocialMediaPostIds,
          selectedSocialMediaPostDuplicateIds: this.buildSelectedSocialMediaPostDuplicateIds(),
          projectOverrides: projectOverrides,
        };

        const response = await this.$store.dispatch("submitDuplication", payload);

        if (response.data.success) {
          this.jobKey = response.data.job_key;

          if (response.data.silent_fail) {
            this.duplicationStatus = "failed";
            this.progressMessage = "WebSocket connection failed.";
            return;
          }

          if (response.data.queue_message) {
            this.queueMessage = response.data.queue_message;
          }

          // Start global monitoring (App.vue handles Echo channel)
          this.$root.$emit("startGlobalDuplicationMonitoring", {
            jobKey: response.data.job_key,
            queuePosition:
              typeof response.data.queue_position === "number"
                ? response.data.queue_position
                : null,
          });
        } else {
          this.duplicationStatus = "failed";
          this.progressMessage = response.data.message || "Failed to start duplication.";
        }
      } catch (error) {
        console.error("Duplication error:", error);
        this.duplicationStatus = "failed";
        this.progressMessage = "Failed to start duplication.";
      }
    },
    handleJobProgress(data) {
      if (this.jobKey && data.jobKey !== this.jobKey) return;

      if (data.status === "pending") {
        this.duplicationStatus = "processing";
        this.progressPercent = 0;
        this.progressDetails = null;
      } else if (data.status === "processing") {
        this.duplicationStatus = "processing";
        this.progressPercent = Math.round(data.progress || 0);
        this.progressMessage = data.message || "Processing...";
        this.progressDetails = data.progress_details || null;
      } else if (data.status === "completed") {
        this.duplicationStatus = "completed";
        this.progressPercent = 100;
        this.progressMessage = data.message || "Project duplicated successfully!";
        this.newProjectNumber = data.project_number || null;
        this.removeBeforeUnloadListener();
        this.$root.$off("duplicationJobProgress", this.handleJobProgress);
        this.$root.$off("duplicationJobInQueue", this.handleJobInQueue);
      } else if (data.status === "failed") {
        this.duplicationStatus = "failed";
        this.progressMessage = data.error || "Duplication failed";
        this.removeBeforeUnloadListener();
        this.$root.$off("duplicationJobProgress", this.handleJobProgress);
        this.$root.$off("duplicationJobInQueue", this.handleJobInQueue);
      }
    },
    handleJobInQueue(data) {
      if (this.jobKey && data.jobKey !== this.jobKey) return;
      if (data.queue_before > 0) {
        this.queueMessage =
          data.queue_before === 1
            ? "You are in the queue. 1 duplication ahead of you."
            : `You are in the queue. ${data.queue_before} duplications ahead of you.`;
      }
    },
    goToNewProject() {
      if (this.newProjectNumber) {
        window.location.href = `/project/${this.newProjectNumber}`;
      }
    },
    goToSourceProject() {
      if (this.sourceProjectNumber) {
        window.location.href = `/project/${this.sourceProjectNumber}`;
      }
    },
    beforeUnloadHandler(e) {
      e.preventDefault();
      e.returnValue = "";
    },
    removeBeforeUnloadListener() {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    },
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener();
    this.$root.$off("duplicationJobProgress", this.handleJobProgress);
    this.$root.$off("duplicationJobInQueue", this.handleJobInQueue);
  },
};
</script>

<style lang="scss" scoped>
.step3-progress{
  .step3-complete{
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}
.step3-finish {
  margin: 30px 50px 0;
}

.step3-summary{
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  border-radius: 15px;
}

.step3-title {
  margin: 0;
  font-weight: 600;
  height: 30px;
  line-height: 30px;
  font-size: .75rem;
  font-weight: 600;
  color: #fff;
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
  background-color: #2d2d2d;
  text-transform: uppercase;
  padding: 0 15px;
}

/* Summary Cards */
.summary-card {
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
}

.summary-card__header {
  background-color: #f7f7f7;
  padding: 10px 16px;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #555;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-card__count {
  background-color: #4C9850;
  color: #fff;
  border-radius: 12px;
  padding: 1px 10px;
  font-size: 12px;
  margin-left: auto;
}

.summary-card__body {
  padding: 12px 16px;
}

.summary-title{
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.summary-item--highlight {
  font-size: 1.6rem;
  color: #333;
}

.summary-item--highlight-sm{
  font-size: .9rem;
  text-transform: uppercase;
  border-bottom: 1px solid #f0f0f0;
  margin: 10px 0 0 0;
  padding: 8px 0;
}

/* Options grid */
.summary-options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 16px;
}

.summary-option {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 3px 0;
}

.summary-option--enabled {
  color: #333;
}

.summary-option--disabled {
  color: #bbb;
}

/* Deliverable list */
.summary-deliverable {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: .9rem;
}

.summary-deliverable-right{
  text-align: left;
  min-width: 140px;
}

.summary-deliverable:last-child {
  border-bottom: none;
}

.summary-deliverable__title {
  flex: 1;
  color: #2d2d2d;
  font-weight: 500;
}

.summary-deliverable__title i {
  color: #999;
  margin-right: 4px;
}

.summary-deliverable__type {
  color: #2d2d2d;
  font-weight: 500;
  font-size: .9rem;
  white-space: nowrap;
}

.summary-deliverable__posts {
  color: #2d2d2d;
  font-weight: 500;
  font-size: 0.9rem;
  white-space: nowrap;
}

/* Totals */
.step3-totals {
  font-size: 13px;
  color: #666;
  margin-bottom: 20px;
  padding: 8px 0;
}

/* Action */
.step3-action {
  text-align: right;
  margin:10px 0 10px;
}

.btn-duplicate {
  display: flex;
  align-items: center;
  gap: 8px;
  // background-color: #4C9850;
  // border-color: #4C9850;
  // color: #fff;
  padding: 6px 10px;
  font-size: .8rem;
  font-weight: 600;
  border-radius: 8px;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
}

.btn-duplicate img{
  width: 28px;
}

.btn-duplicate:hover:not(:disabled) {
  // background-color: #3d7f42;
  // border-color: #3d7f42;
  color: #fff;
}

.btn-duplicate:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel{
  background-color: var(--wizard-cancel-bg);
  border-color: var(--wizard-cancel-border);
  color: #fff;
  padding: 6px 10px;
  font-size: .8rem;
  font-weight: 600;
  border-radius: 8px;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
}

/* Progress */
.step3-progress {
  margin-top: 16px;
  padding: 20px;
  border-radius: 8px;
}

.step3-queue-msg {
  color: #666;
}

.step3-progress-info {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #555;
}

.step3-progress-count {
  font-weight: 700;
  color: #4C9850;
}

.step3-progress-label {
  color: #888;
  font-style: italic;
}

/* Complete */
.step3-complete-message {
  font-weight: 600;
  font-size: 16px;
  color: #333;
}

/* Failed */
.step3-failed p {
  margin-bottom: 4px;
}
</style>
